import React, { useState, useEffect } from 'react';
import { Plus, Building, Calendar, Clock, MapPin, CheckCircle, XCircle, AlertTriangle, Edit, Trash2, Briefcase, TrendingUp } from 'lucide-react';
import { staffAPI } from '../services/api';
import AdvancedFilters from '../components/UI/AdvancedFilters';
import SelfServiceListSkeleton from '../components/UI/SelfServiceListSkeleton';
import Modal from '../components/UI/Modal';
import toast from '../utils/toast';
import ConfirmModal from '../components/UI/ConfirmModal';
import { formatDate } from '../utils/dateUtils';
import { useTabPersistence } from '../hooks/usePersistence';
import FormSelect from '../components/UI/FormSelect';

const WorkExperience = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const [statistics, setStatistics] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [viewMode, setViewMode] = useTabPersistence('work-experience-view', 'cards', ['cards', 'timeline'], 'view');

  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    job_title: '',
    department: '',
    start_date: '',
    end_date: '',
    employment_type: 'full_time',
    industry: '',
    location: '',
    description: '',
    key_achievements: '',
    technologies_used: '',
    verification_status: 'pending',
    reference_contact: '',
    reference_email: '',
    reference_phone: '',
    salary_range: '',
    is_current_position: false
  });

  // Employment types
  const employmentTypes = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'internship', label: 'Internship' },
    { value: 'temporary', label: 'Temporary' },
    { value: 'volunteer', label: 'Volunteer' }
  ];

  // Industries
  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'construction', label: 'Construction' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'government', label: 'Government' },
    { value: 'non_profit', label: 'Non-Profit' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'other', label: 'Other' }
  ];

  const verificationStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'not_required', label: 'Not Required' }
  ];

  // Filter configuration
  const filterConfig = {
    searchFields: ['company_name', 'job_title', 'department', 'location', 'technologies_used'],
    choiceFilters: [
      { key: 'industry', label: 'Industry', options: industries },
      { key: 'employment_type', label: 'Employment Type', options: employmentTypes },
      { key: 'verification_status', label: 'Verification Status', options: verificationStatuses }
    ],
    dateFilters: [
      { key: 'start_date', label: 'Start Date Range' },
      { key: 'end_date', label: 'End Date Range' }
    ],
    booleanFilters: [
      { key: 'is_current_position', label: 'Current Position' },
      { key: 'has_reference', label: 'Has Reference Contact' }
    ],
    rangeFilters: [
      { key: 'duration_months', label: 'Duration (Months)', min: 1, max: 240 }
    ]
  };

  useEffect(() => {
    fetchExperiences();
    fetchStatistics();
  }, []);

  const fetchExperiences = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await staffAPI.getWorkExperiences(filters);
      setExperiences(response.data.results || response.data);
    } catch (error) {
      toast.error('Failed to fetch work experiences');
      console.error('Error fetching work experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await staffAPI.getWorkExperienceStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleFilterChange = (filters) => {
    fetchExperiences(filters);
  };

  const handleAddExperience = async (e) => {
    e.preventDefault();
    try {
      await staffAPI.createWorkExperience(formData);
      toast.success('Work experience added successfully');
      setShowAddForm(false);
      resetForm();
      fetchExperiences();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to add work experience');
      console.error('Error adding work experience:', error);
    }
  };

  const handleEditExperience = async (e) => {
    e.preventDefault();
    try {
      await staffAPI.updateWorkExperience(editingExperience.id, formData);
      toast.success('Work experience updated successfully');
      setEditingExperience(null);
      resetForm();
      fetchExperiences();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to update work experience');
      console.error('Error updating work experience:', error);
    }
  };

  const handleDeleteExperience = (id) => {
    setConfirmAction({ id });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await staffAPI.deleteWorkExperience(confirmAction.id);
      toast.success('Work experience deleted successfully');
      fetchExperiences();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to delete work experience');
      console.error('Error deleting work experience:', error);
    } finally {
      setConfirmAction(null);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      job_title: '',
      department: '',
      start_date: '',
      end_date: '',
      employment_type: 'full_time',
      industry: '',
      location: '',
      description: '',
      key_achievements: '',
      technologies_used: '',
      verification_status: 'pending',
      reference_contact: '',
      reference_email: '',
      reference_phone: '',
      salary_range: '',
      is_current_position: false
    });
  };

  const startEdit = (experience) => {
    setEditingExperience(experience);
    setFormData({
      company_name: experience.company_name || '',
      job_title: experience.job_title || '',
      department: experience.department || '',
      start_date: experience.start_date || '',
      end_date: experience.end_date || '',
      employment_type: experience.employment_type || 'full_time',
      industry: experience.industry || '',
      location: experience.location || '',
      description: experience.description || '',
      key_achievements: experience.key_achievements || '',
      technologies_used: experience.technologies_used || '',
      verification_status: experience.verification_status || 'pending',
      reference_contact: experience.reference_contact || '',
      reference_email: experience.reference_email || '',
      reference_phone: experience.reference_phone || '',
      salary_range: experience.salary_range || '',
      is_current_position: experience.is_current_position || false
    });
    setShowAddForm(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'rejected': return 'text-red-600 bg-red-50';
      case 'not_required': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getIndustryColor = (industry) => {
    const colors = {
      technology: 'bg-blue-100 text-blue-800',
      healthcare: 'bg-red-100 text-red-800',
      finance: 'bg-green-100 text-green-800',
      construction: 'bg-orange-100 text-orange-800',
      education: 'bg-purple-100 text-purple-800',
      retail: 'bg-pink-100 text-pink-800',
      manufacturing: 'bg-indigo-100 text-indigo-800',
      hospitality: 'bg-yellow-100 text-yellow-800',
      government: 'bg-gray-100 text-gray-800',
      non_profit: 'bg-teal-100 text-teal-800',
      consulting: 'bg-cyan-100 text-cyan-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[industry] || colors.other;
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return 'N/A';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const months = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30));
    
    if (months < 12) {
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return `${years} year${years > 1 ? 's' : ''}${remainingMonths > 0 ? ` ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}` : ''}`;
    }
  };

  const StatisticsCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
        </div>
      </div>
    </div>
  );

  const TimelineView = () => (
    <div className="relative">
      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
      {experiences.map((experience, index) => (
        <div key={experience.id} className="relative flex items-start space-x-4 pb-8">
          <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center border-4 border-white shadow">
            <Building className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1 bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{experience.job_title}</h3>
                <p className="text-blue-600 font-medium">{experience.company_name}</p>
                <p className="text-sm text-gray-600">{experience.department}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIndustryColor(experience.industry)}`}>
                    {experience.industry}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(experience.verification_status)}`}>
                    {experience.verification_status}
                  </span>
                  {experience.is_current_position && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Current
                    </span>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(experience)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteExperience(experience.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                <span>{formatDate(experience.start_date)} - {formatDate(experience.end_date)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                <span>{calculateDuration(experience.start_date, experience.end_date)}</span>
              </div>
              {experience.location && (
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  <span>{experience.location}</span>
                </div>
              )}
              <div className="flex items-center">
                <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                <span>{experience.employment_type}</span>
              </div>
            </div>

            {experience.description && (
              <p className="text-gray-600 mb-3">{experience.description}</p>
            )}

            {experience.key_achievements && (
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-2">Key Achievements</h4>
                <p className="text-gray-600">{experience.key_achievements}</p>
              </div>
            )}

            {experience.technologies_used && (
              <div className="mb-3">
                <h4 className="font-medium text-gray-900 mb-2">Technologies Used</h4>
                <p className="text-gray-600">{experience.technologies_used}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return <SelfServiceListSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Work Experience</h1>
        <p className="text-gray-600">Track your professional journey and career progression</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <StatisticsCard
          title="Total Positions"
          value={statistics.total_positions || 0}
          icon={Briefcase}
          color="blue"
        />
        <StatisticsCard
          title="Years Experience"
          value={statistics.total_years_experience || 0}
          icon={Clock}
          color="green"
        />
        <StatisticsCard
          title="Verified Positions"
          value={statistics.verified_positions || 0}
          icon={CheckCircle}
          color="purple"
        />
        <StatisticsCard
          title="Industries"
          value={statistics.unique_industries || 0}
          icon={TrendingUp}
          color="orange"
        />
      </div>

      {/* Advanced Filters */}
      <div className="mb-6">
        <AdvancedFilters
          config={filterConfig}
          onFilterChange={handleFilterChange}
          resultCount={experiences.length}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Experience
        </button>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('cards')}
            className={`px-4 py-2 rounded-lg ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Cards
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-4 py-2 rounded-lg ${viewMode === 'timeline' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Timeline
          </button>
        </div>
      </div>

      {/* Work Experience Display */}
      {viewMode === 'timeline' ? (
        <TimelineView />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {experiences.map(experience => (
            <div key={experience.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {experience.job_title}
                  </h3>
                  <p className="text-blue-600 font-medium mb-1">{experience.company_name}</p>
                  <p className="text-sm text-gray-600 mb-2">{experience.department}</p>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIndustryColor(experience.industry)}`}>
                      {experience.industry}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(experience.verification_status)}`}>
                      {experience.verification_status}
                    </span>
                    {experience.is_current_position && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Current
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEdit(experience)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteExperience(experience.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  <span>{formatDate(experience.start_date)} - {formatDate(experience.end_date)}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-gray-400" />
                  <span>{calculateDuration(experience.start_date, experience.end_date)}</span>
                </div>
                {experience.location && (
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{experience.location}</span>
                  </div>
                )}
              </div>

              {experience.description && (
                <p className="text-sm text-gray-600 mb-3 border-t pt-3">
                  {experience.description}
                </p>
              )}

              {experience.technologies_used && (
                <div className="text-sm">
                  <span className="font-medium text-gray-900">Technologies: </span>
                  <span className="text-gray-600">{experience.technologies_used}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {experiences.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No work experience found</h3>
          <p className="text-gray-600">Add your first work experience to get started</p>
        </div>
      )}

      {/* Add/Edit Experience Modal */}
      <Modal
        isOpen={showAddForm}
        onClose={() => { setShowAddForm(false); setEditingExperience(null); resetForm(); }}
        title={editingExperience ? 'Edit Work Experience' : 'Add Work Experience'}
        size="lg"
        theme="employee"
      >
            <form onSubmit={editingExperience ? handleEditExperience : handleAddExperience}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type *
                  </label>
                  <FormSelect
                    value={formData.employment_type}
                    onChange={(val) => setFormData({...formData, employment_type: val})}
                    options={employmentTypes}
                    theme="employee"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry *
                  </label>
                  <FormSelect
                    value={formData.industry}
                    onChange={(val) => setFormData({...formData, industry: val})}
                    options={[
                      { value: '', label: 'Select industry' },
                      ...industries
                    ]}
                    theme="employee"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    disabled={formData.is_current_position}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Status
                  </label>
                  <FormSelect
                    value={formData.verification_status}
                    onChange={(val) => setFormData({...formData, verification_status: val})}
                    options={verificationStatuses}
                    theme="employee"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salary Range
                  </label>
                  <input
                    type="text"
                    value={formData.salary_range}
                    onChange={(e) => setFormData({...formData, salary_range: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    placeholder="e.g., 50,000 - 70,000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Contact
                  </label>
                  <input
                    type="text"
                    value={formData.reference_contact}
                    onChange={(e) => setFormData({...formData, reference_contact: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Email
                  </label>
                  <input
                    type="email"
                    value={formData.reference_email}
                    onChange={(e) => setFormData({...formData, reference_email: e.target.value})}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key Achievements
                  </label>
                  <textarea
                    value={formData.key_achievements}
                    onChange={(e) => setFormData({...formData, key_achievements: e.target.value})}
                    rows={3}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Technologies Used
                  </label>
                  <textarea
                    value={formData.technologies_used}
                    onChange={(e) => setFormData({...formData, technologies_used: e.target.value})}
                    rows={2}
                    className="w-full p-2 border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    placeholder="e.g., JavaScript, React, Node.js, Python"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_current_position}
                      onChange={(e) => setFormData({...formData, is_current_position: e.target.checked, end_date: e.target.checked ? '' : formData.end_date})}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">This is my current position</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingExperience(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingExperience ? 'Update' : 'Add'} Experience
                </button>
              </div>
            </form>
      </Modal>

      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Work Experience"
        message="Are you sure you want to delete this work experience?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmAction(null)}
        theme="employee"
      />
    </div>
  );
};

export default WorkExperience; 